import React, { useEffect, useState } from 'react';
import { estimates as estimatesApi, emails as emailsApi } from '@/utils/api';
import { Plus, Share2, CheckCircle, XCircle, X, FileText, Download, Mail, Tag, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CatalogPicker from '@/components/CatalogPicker';

const STATUS_STYLES = {
  Draft: { bg: '#E8E3D9', color: '#5C6B72' },
  Sent: { bg: '#E6F1F4', color: '#006080' },
  Approved: { bg: '#3C8A5A15', color: '#3C8A5A' },
  'Changes Requested': { bg: '#D9A44115', color: '#D9A441' },
};

export default function EstimatesTab({ projectId, project, isOperator }) {
  const [estList, setEstList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [lineItems, setLineItems] = useState([{ description: '', hours: 0, rate: 150, amount: 0, notes: '', category: '', catalog_item_id: null }]);
  const [form, setForm] = useState({ currency: 'USD', tax_rate: 0, discount: 0, terms: '', notes: '' });
  const [rejectNote, setRejectNote] = useState('');
  const [rejectingId, setRejectingId] = useState(null);

  useEffect(() => {
    estimatesApi.list(projectId)
      .then(r => setEstList(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const subtotal = lineItems.reduce((s, li) => s + (li.amount || li.hours * li.rate || 0), 0);
  const tax = subtotal * (form.tax_rate / 100);
  const total = subtotal + tax - (form.discount || 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const items = lineItems.map(li => ({
        ...li,
        amount: li.amount || li.hours * li.rate,
      }));
      const r = await estimatesApi.create(projectId, { ...form, project_id: projectId, line_items: items });
      setEstList(prev => [r.data, ...prev]);
      setShowCreate(false);
      toast.success('Estimate created');
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed';
      toast.error(msg);
    }
  };

  const handleShare = async (estId) => {
    try {
      await estimatesApi.share(projectId, estId);
      setEstList(prev => prev.map(e => e.id === estId ? { ...e, is_shared: true, status: 'Sent' } : e));
      toast.success('Estimate shared with client');
    } catch { toast.error('Failed'); }
  };

  const handleApprove = async (estId) => {
    try {
      await estimatesApi.approve(projectId, estId, 'Approved via portal');
      setEstList(prev => prev.map(e => e.id === estId ? { ...e, status: 'Approved' } : e));
      toast.success('Estimate approved');
    } catch { toast.error('Failed'); }
  };

  const handleReject = async (estId) => {
    try {
      await estimatesApi.reject(projectId, estId, rejectNote);
      setEstList(prev => prev.map(e => e.id === estId ? { ...e, status: 'Changes Requested' } : e));
      setRejectingId(null);
      setRejectNote('');
      toast.success('Changes requested');
    } catch { toast.error('Failed'); }
  };

  const updateLineItem = (i, field, val) => {
    const updated = [...lineItems];
    updated[i] = { ...updated[i], [field]: ['description', 'notes', 'category'].includes(field) ? val : Number(val) };
    if (['hours', 'rate'].includes(field)) updated[i].amount = updated[i].hours * updated[i].rate;
    setLineItems(updated);
  };

  const pickFromCatalog = (i, item) => {
    const updated = [...lineItems];
    updated[i] = {
      ...updated[i],
      description: item.name + (item.description ? ` — ${item.description}` : ''),
      rate: item.default_price || updated[i].rate,
      hours: item.default_hours || updated[i].hours || 1,
      category: item.category || '',
      catalog_item_id: item.id || null,
    };
    updated[i].amount = updated[i].hours * updated[i].rate;
    setLineItems(updated);
  };

  const removeLineItem = (i) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Estimates</h3>
        {isOperator && (
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => setShowCreate(true)} data-testid="create-estimate-button">
            <Plus size={13} /> New Estimate
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1].map(i => <div key={i} className="h-24 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
      ) : estList.length === 0 ? (
        <div className="card-base p-8 text-center">
          <FileText size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No estimates yet</p>
          {isOperator && <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Create an estimate and share it with the client</p>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="estimates-list">
          {estList.map(est => {
            const st = STATUS_STYLES[est.status] || STATUS_STYLES.Draft;
            return (
              <div key={est.id} className="card-base p-4" data-testid={`estimate-${est.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="badge-status" style={{ background: st.bg, color: st.color }}>{est.status}</span>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>v{est.version} · {new Date(est.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black" style={{ color: 'var(--ink)' }}>{est.currency} {est.total?.toLocaleString()}</p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Subtotal: {est.subtotal?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Line items */}
                <div className="space-y-1 mb-3">
                  {est.line_items?.map((li, i) => (
                    <div key={i} className="py-1.5 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: 'var(--ink)' }} className="flex items-center gap-2">
                          {li.description}
                          {li.category && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: '#006080' }}>{li.category}</span>
                          )}
                        </span>
                        <span style={{ color: 'var(--muted)' }}>{est.currency} {(li.amount || li.hours * li.rate)?.toLocaleString()}</span>
                      </div>
                      {li.notes && (
                        <p className="text-xs mt-0.5 flex items-start gap-1" style={{ color: 'var(--muted)' }}>
                          <MessageSquare size={10} className="mt-0.5 flex-shrink-0" /> <span>{li.notes}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1 font-semibold transition-colors" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => window.open(estimatesApi.pdfUrl(projectId, est.id), '_blank')} data-testid={`download-estimate-${est.id}`}>
                    <Download size={12} /> PDF
                  </button>
                  {isOperator && est.status === 'Draft' && (
                    <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1 font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => handleShare(est.id)} data-testid={`share-estimate-${est.id}`}>
                      <Share2 size={12} /> Share with Client
                    </button>
                  )}
                  {isOperator && est.is_shared && (
                    <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1 font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} onClick={async () => {
                      try {
                        const r = await emailsApi.sendEstimate(est.id);
                        if (r.data.status === 'sent') toast.success('Estimate emailed to client');
                        else toast.error(r.data.error || 'Configure brand SMTP first');
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }} data-testid={`email-estimate-${est.id}`}>
                      <Mail size={12} /> Email Client
                    </button>
                  )}
                  {!isOperator && est.status === 'Sent' && (
                    <>
                      <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1 font-semibold" style={{ background: '#3C8A5A15', color: '#3C8A5A' }} onClick={() => handleApprove(est.id)} data-testid={`approve-estimate-${est.id}`}>
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1 font-semibold" style={{ background: '#D9A44115', color: '#D9A441' }} onClick={() => setRejectingId(est.id)} data-testid={`reject-estimate-${est.id}`}>
                        <XCircle size={12} /> Request Changes
                      </button>
                    </>
                  )}
                </div>

                {rejectingId === est.id && (
                  <div className="mt-3 flex gap-2">
                    <input className="input-agency text-sm flex-1" placeholder="What needs to change?" value={rejectNote} onChange={e => setRejectNote(e.target.value)} />
                    <button className="btn-primary text-xs px-3" onClick={() => handleReject(est.id)}>Send</button>
                    <button className="p-1.5" style={{ color: 'var(--muted)' }} onClick={() => setRejectingId(null)}><X size={14} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create estimate modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-2xl p-5 animate-fade-in overflow-y-auto max-h-[90vh]" data-testid="create-estimate-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>New Estimate</h3>
              <button onClick={() => setShowCreate(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Line Items</label>
                  <button type="button" className="text-xs" style={{ color: '#006080' }} onClick={() => setLineItems(prev => [...prev, { description: '', hours: 0, rate: 150, amount: 0, notes: '', category: '', catalog_item_id: null }])} data-testid="add-line-item-button">+ Add Line</button>
                </div>
                <div className="space-y-3">
                  {lineItems.map((li, i) => (
                    <div key={i} className="p-2 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`line-item-${i}`}>
                      <div className="grid grid-cols-12 gap-1.5 items-center">
                        <input className="input-agency text-sm col-span-6" placeholder="Description" value={li.description} onChange={e => updateLineItem(i, 'description', e.target.value)} required data-testid={`line-desc-${i}`} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" step="0.25" min="0" placeholder="Hrs" value={li.hours} onChange={e => updateLineItem(i, 'hours', e.target.value)} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" min="0" placeholder="Rate" value={li.rate} onChange={e => updateLineItem(i, 'rate', e.target.value)} />
                        <div className="col-span-1 text-xs tabular-nums text-right font-semibold" style={{ color: 'var(--ink)' }}>{(li.amount || li.hours * li.rate).toFixed(2)}</div>
                        <button type="button" onClick={() => removeLineItem(i)} className="col-span-1 p-1" style={{ color: lineItems.length > 1 ? '#B34A3A' : 'var(--border)' }} disabled={lineItems.length <= 1}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <CatalogPicker context="estimate" onPick={(it) => pickFromCatalog(i, it)} />
                        {li.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: '#E6F1F4', color: '#006080' }}>
                            <Tag size={8} /> {li.category}
                          </span>
                        )}
                        <input className="flex-1 text-xs px-2 py-1.5 rounded-btn border outline-none" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} placeholder="Comment / remark for this line (optional)" value={li.notes || ''} onChange={e => updateLineItem(i, 'notes', e.target.value)} data-testid={`line-notes-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Tax %</label><input className="input-agency text-sm" type="number" min="0" max="100" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: Number(e.target.value)})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Discount</label><input className="input-agency text-sm" type="number" min="0" value={form.discount} onChange={e => setForm({...form, discount: Number(e.target.value)})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Currency</label>
                  <select className="input-agency text-sm" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
                    {['USD','EUR','GBP','INR','AUD','CAD'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="p-3 rounded-btn flex justify-between items-center" style={{ background: 'var(--bg)' }}>
                <div className="text-sm space-y-0.5" style={{ color: 'var(--muted)' }}>
                  <p>Subtotal: {form.currency} {subtotal.toLocaleString()}</p>
                  {form.tax_rate > 0 && <p>Tax ({form.tax_rate}%): {form.currency} {tax.toFixed(2)}</p>}
                  {form.discount > 0 && <p>Discount: -{form.currency} {form.discount}</p>}
                </div>
                <p className="text-xl font-black" style={{ color: 'var(--ink)' }}>{form.currency} {total.toFixed(2)}</p>
              </div>

              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Notes</label><textarea className="input-agency text-sm resize-none" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>

              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="create-estimate-submit">Create Estimate</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
